// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { EclecticSection, EclecticPlayerStanding, TourGameRound } from './types';

export type EclecticRow = EclecticPlayerStanding & { name: string };

// Standings rows in backend rank order, with display names resolved.
export function buildEclecticRows(
  section: EclecticSection | null,
  nameLookup: Map<string, string>,
  fallbackName: string,
): EclecticRow[] {
  if (!section) return [];
  return section.standings.map((standing) => ({
    ...standing,
    name: nameLookup.get(standing.playerId) ?? fallbackName,
  }));
}

// 1-based round index by round id (for compact "R2"-style source labels).
export function buildRoundIndexById(rounds: TourGameRound[]): Map<string, number> {
  const index = new Map<string, number>();
  rounds.forEach((round, i) => index.set(round.roundId, i + 1));
  return index;
}

// A snapshot of Eclectic standings as they stood after the given round, using
// only the points banked in that round and earlier ones. The backend section is
// always the tour's current (possibly later) tally, so the round-summary screen
// uses this to show what Eclectic looked like at the round being viewed instead
// of always the latest result. isFinalRound/isFinal are scoped the same way —
// true only when the round being viewed is itself the tour's last round, not
// just because the tour has since reached its last round. Falls back to the
// unscoped section when the round id is missing or not part of the tour's
// Eclectic rounds.
export function buildEclecticSectionThroughRound(
  section: EclecticSection | null,
  roundId: string | null | undefined
): EclecticSection | null {
  if (!section) return null;
  const target = roundId?.trim().toLowerCase();
  if (!target) return section;
  const roundIds = section.rounds.map((round) => round.roundId.trim().toLowerCase());
  const cutoffIndex = roundIds.indexOf(target);
  if (cutoffIndex < 0) return section;
  const includedRoundIds = new Set(roundIds.slice(0, cutoffIndex + 1));
  const isLastRound = cutoffIndex === roundIds.length - 1;

  const rescored = section.standings.map((standing) => {
    const holes = standing.holes.map((hole) => {
      const eligible = hole.pointsByRound.filter((entry) =>
        includedRoundIds.has(entry.roundId.trim().toLowerCase())
      );
      if (eligible.length === 0) {
        return { ...hole, bestPoints: 0, bestRoundId: null };
      }
      const best = eligible.reduce((max, entry) => (entry.points > max.points ? entry : max));
      return { ...hole, bestPoints: best.points, bestRoundId: best.roundId };
    });
    return { ...standing, holes, totalPoints: holes.reduce((sum, hole) => sum + hole.bestPoints, 0) };
  });

  // Stable sort: ties keep the backend's original relative order rather than
  // inventing a new tie-break.
  const ordered = [...rescored].sort((a, b) => b.totalPoints - a.totalPoints);
  let lastPoints: number | null = null;
  let lastPosition = 1;
  const standings = ordered.map((standing, index) => {
    const position = standing.totalPoints === lastPoints ? lastPosition : index + 1;
    lastPoints = standing.totalPoints;
    lastPosition = position;
    return { ...standing, position };
  });

  return {
    ...section,
    isFinal: section.isFinal && isLastRound,
    isFinalRound: isLastRound,
    standings,
  };
}

// Best Eclectic points a player already banked on a hole in the tour rounds that
// came *before* the given round — the number to beat when playing the hole again.
// Empty for the tour's first round (nothing banked yet) or for an unknown round id.
export function buildEclecticBestBeforeRound(
  section: EclecticSection | null,
  holeNumber: number,
  currentRoundId: string | null | undefined
): Map<string, number> {
  const bestByPlayerId = new Map<string, number>();
  const current = currentRoundId?.trim().toLowerCase();
  if (!section || !current) return bestByPlayerId;
  const roundIds = section.rounds.map((round) => round.roundId.trim().toLowerCase());
  const currentIndex = roundIds.indexOf(current);
  if (currentIndex < 1) return bestByPlayerId;
  const priorRoundIds = new Set(roundIds.slice(0, currentIndex));
  section.standings.forEach((standing) => {
    const hole = standing.holes.find((cell) => cell.holeNumber === holeNumber);
    if (!hole) return;
    const priorPoints = hole.pointsByRound
      .filter((entry) => priorRoundIds.has(entry.roundId.trim().toLowerCase()))
      .map((entry) => entry.points);
    if (priorPoints.length === 0) return;
    bestByPlayerId.set(standing.playerId, Math.max(...priorPoints));
  });
  return bestByPlayerId;
}
